<template>
  <ResidentialFloorDetail
    v-if="!!this.selectedData?.flat_id"
    @goBack="this.resetSelected"
    :activeData="this.selectedData"
    :params="this.filters"
    :updateData="this.onFLoorSchemaUpdated"
  />
  <div v-show="!this.selectedData?.flat_id" class="wrapper">
    <q-linear-progress v-if="this.isFetching" indeterminate />
    <PageTitle>Настройка объекта</PageTitle>
    <div
      style="width: 100%"
    >
      <div class="header">
        <Filters 
          :filters="this.filters" 
          :onChange="this.onFiltersChange" 
          @residentsLoaded="onResidentsLoaded"
          @projectTypesLoaded="onProjectTypesLoaded"
          @entrancesLoaded="onEntrancesLoaded"
        />
        <CustomDropdown
          v-if="!!this.filters.project_entrance_id && !!this.filters.project_type_id"
          :onClick="this.handleAction"
          :loading="this.dropdownLoading"
          :options="this.actions || []"
          title="Действия"
        />
      </div>
      <transition name="floors-mismatch">
        <div
          v-if="this.floorsMismatchLoading && this.floorsPlan?.length"
          class="floors-mismatch-placeholder tw-mb-2"
        >
          <q-linear-progress indeterminate size="2px" class="tw-mb-1" />
          <span class="text-caption text-grey-7">Проверка соответствия этажей Ревит и цифрового проекта…</span>
        </div>
      </transition>
      <transition name="floors-mismatch">
        <q-banner
          v-if="!this.floorsMismatchLoading && this.floorsMismatch?.only_in_structure?.length > 0"
          class="floors-mismatch-banner bg-blue-1 text-grey-9 tw-mb-2"
          style="border-left: 4px solid #1976d2;"
        >
          <template v-slot:avatar>
            <q-icon name="info" color="primary" />
          </template>
          <span>Есть этажи в Ревите (структуре), которых нет в цифровом проекте</span>
          <span v-if="this.floorsMismatch.only_in_structure.length">: {{ this.floorsMismatch.only_in_structure.join(', ') }}</span>
        </q-banner>
      </transition>
      <transition name="floors-mismatch">
        <q-banner
          v-if="!this.floorsMismatchLoading && this.floorsMismatch?.only_in_digital?.length > 0"
          class="floors-mismatch-banner bg-amber-1 text-grey-9 tw-mb-2"
          style="border-left: 4px solid #ed6c02;"
        >
          <template v-slot:avatar>
            <q-icon name="info" color="orange" />
          </template>
          <span>Есть этажи в цифровом проекте, которых нет в Ревите (структуре)</span>
          <span v-if="this.floorsMismatch.only_in_digital.length">: {{ this.floorsMismatch.only_in_digital.join(', ') }}</span>
        </q-banner>
      </transition>
      <div v-if="effectiveProjectId" class="tw-mb-2 tw-pl-4">
        <span class="project-id-badge">ID проекта {{ effectiveProjectId }}</span>
      </div>
      <div class="tw-flex tw-justify-between tw-gap-2 tw-pr-4">
        <div class="tw-flex-1">
          <ResidentialApartmentsList
            :onApartmentClick="this.onApartmentClick"
            :floorsPlan="this.floorsPlan"
            :getFloorData="this.getFloorData"
            :smetaLoading="this.smetaLoading"
            :isFetching="this.isFetching"
            :show="!!this.filters.project_entrance_id && !!this.filters.project_type_id"
            :checkboxMode="true"
            :updateData="this.onFLoorsPlanUpdated"
            :params="this.filters"
            :onDeleteFloor="handleDeleteFloor"
            @refresh="handleRefresh"
          />
        </div>
        <div class="tw-w-[500px]"
            v-if="!!this.filters.project_entrance_id && !!this.filters.project_type_id">
          <Legend
            :legend-fetching="this.legendFetching"
            :legend-info="this.legendData"
            :reload-data="this.getLegend" />
        </div>
      </div>
    </div>
  </div>
  
  <!-- Диалог выбора причины изменения сметы -->
  <q-dialog v-model="showReasonDialog" persistent>
    <q-card style="min-width: 700px; max-width: 900px; max-height: 90vh;">
      <q-card-section>
        <div class="text-h6">Пересчёт сметы</div>
        <div class="text-caption text-grey">Выбрано этажей: {{ pendingFloors.length }}</div>
      </q-card-section>

      <q-card-section class="q-pt-none" style="max-height: 70vh; overflow-y: auto;">
        <!-- Предупреждение -->
        <q-banner class="bg-orange-1 text-grey-9" style="border-left: 4px solid #ef6c00; margin-bottom: 16px;">
          <template v-slot:avatar>
            <q-icon name="warning" color="orange" />
          </template>
          <div style="font-size: 13px; line-height: 1.5;">
            <strong>⚠️ Внимание!</strong> Пересчёт заново берёт материалы и работы из пакета.
            Если была сделана замена материала — изменения будут потеряны.
            <br/>
            <strong>Работы с АВР (Актом выполненных работ) НЕ будут пересчитаны.</strong>
          </div>
        </q-banner>

        <!-- Блок АВР работ -->
        <div v-if="avrLoading" class="q-pa-md text-center">
          <q-spinner color="primary" size="2em" />
          <div class="q-mt-sm text-grey">Загрузка информации об АВР...</div>
        </div>
        
        <q-expansion-item
          v-else-if="avrLockedWorks.length > 0"
          icon="lock"
          label="Работы с АВР (не будут пересчитаны)"
          :caption="`${avrSummary.worksCount} работ на сумму ${formatNumber(avrSummary.totalSum)} ₸`"
          header-class="bg-blue-1"
          style="margin-bottom: 16px; border-radius: 8px; border: 1px solid #e0e0e0;"
        >
          <q-card>
            <q-card-section class="q-pa-sm">
              <!-- Группировка по АВР документам -->
              <div v-for="avr in avrGroupedByDocument" :key="avr.floor_map_document_id" class="q-mb-md">
                <q-expansion-item
                  dense
                  :label="`${avr.avr_code}`"
                  :caption="`от ${formatDate(avr.avr_date)} • ${avr.works.length} работ на ${formatNumber(avr.total_sum)} ₸`"
                  header-class="bg-grey-2"
                >
                  <q-list dense separator>
                    <q-item v-for="(work, idx) in avr.works" :key="idx" class="q-pa-xs">
                      <q-item-section>
                        <q-item-label class="text-body2">{{ work.work_set_name }}</q-item-label>
                      </q-item-section>
                      <q-item-section side>
                        <q-item-label class="text-weight-medium">{{ formatNumber(work.total_sum) }} ₸</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-expansion-item>
              </div>
            </q-card-section>
          </q-card>
        </q-expansion-item>

        <!-- Блок работ, которые можно пересчитать (группировка по этажам) -->
        <q-expansion-item
          v-if="recalculableWorks.length > 0"
          icon="refresh"
          label="Работы для пересчёта"
          :caption="`${recalculableSummary.worksCount} работ на сумму ${formatNumber(recalculableSummary.totalSum)} ₸`"
          header-class="bg-green-1"
          style="margin-bottom: 16px; border-radius: 8px; border: 1px solid #e0e0e0;"
        >
          <q-card>
            <q-card-section class="q-pa-sm">
              <!-- Группировка по этажам -->
              <div v-for="floor in recalculableGroupedByFloor" :key="floor.floor_map_id" class="q-mb-md">
                <q-expansion-item
                  dense
                  :label="`Этаж ${floor.floor}`"
                  :caption="`${floor.works.length} работ на ${formatNumber(floor.total_sum)} ₸`"
                  header-class="bg-grey-2"
                >
                  <q-list dense separator>
                    <q-item v-for="(work, idx) in floor.works" :key="idx" class="q-pa-xs">
                      <q-item-section>
                        <q-item-label class="text-body2">{{ work.work_set_name }}</q-item-label>
                      </q-item-section>
                      <q-item-section side>
                        <q-item-label class="text-weight-medium">{{ formatNumber(work.total_sum) }} ₸</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-expansion-item>
              </div>
            </q-card-section>
          </q-card>
        </q-expansion-item>

        <!-- Сообщение если смета ни разу не пересчитывалась -->
        <q-banner v-else-if="smetaNeverCalculated" class="bg-orange-1" style="border-left: 4px solid #ff9800; margin-bottom: 16px;">
          <template v-slot:avatar>
            <q-icon name="info" color="orange" />
          </template>
          <div style="font-size: 13px;">
            <strong>Смета ещё не рассчитывалась.</strong><br/>
            Выберите этажи и нажмите "Пересчитать смету" для первичного расчёта.
          </div>
        </q-banner>

        <!-- Выбор причины -->
        <q-select
          v-model="selectedReason"
          :options="reasonOptions"
          option-value="smeta_change_reason_id"
          option-label="reason_name"
          label="Выберите причину пересчёта"
          outlined
          emit-value
          map-options
          style="margin-bottom: 16px;"
        >
          <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.reason_name }}</q-item-label>
                <q-item-label caption>{{ scope.opt.description }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-badge :color="scope.opt.recalc_type === 'hard' ? 'red' : 'blue'" :label="scope.opt.recalc_type === 'hard' ? 'жёсткий' : 'мягкий'" />
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        
        <q-input
          v-model="versionComments"
          type="textarea"
          label="Комментарий к версии сметы (опционально)"
          outlined
          rows="2"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Отмена" color="grey" @click="cancelRecalc" />
        <q-btn 
          flat 
          label="Пересчитать" 
          color="primary" 
          @click="confirmRecalc"
          :disable="!selectedReason"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Диалог подгрузки Revit проекта (пол экрана) -->
  <q-dialog v-model="loadRevitDialogOpen" persistent>
    <q-card class="load-revit-dialog-card">
      <q-card-section class="row items-center q-pb-none">
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>
      <q-card-section class="load-revit-dialog-body">
        <ProjectLoadRevit
          v-if="loadRevitDialogOpen && effectiveProjectId && filters.project_entrance_id"
          :project-id="effectiveProjectId"
          :project-type-id="filters.project_type_id"
          :initial-project-entrance-id="filters.project_entrance_id"
          @close="loadRevitDialogOpen = false"
          @success="() => { loadRevitDialogOpen = false; getFloorsPlan(); loadFloorsMismatch(); }"
        />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import CustomDropdown from "@/components/common/CustomDropdown";
import PageTitle from "@/components/common/PageTitle.vue";
import CustomSelect from "@/components/form/CustomSelect";
import { MODAL_NAMES } from "@/constants";
import Filters from './filters.vue';
import Legend from './Legend.vue';
import ResidentialApartmentsList from './ResidentialApartmentsList.vue';
import ResidentialFloorDetail from './ResidentialFloorDetail.vue';
import { createFloorObject, getEntranceApartments, getEntranceLegendInfo, getEntranceFloorsMismatch, recalculateSmeta, createSmetaVersion, getAvrLockedWorks, getRecalculableWorks } from './services';
import { getSmetaChangeReasons, deleteProjectFloor, createDS } from '../ProjectsEntry/services';
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from "@/services/store/app";
import { showErrorToast } from "@/services";
import ProjectLoadRevit from '../ProjectsEntry/ProjectLoadRevit.vue';

export default {
  name: 'BuildingSettings',
  components: { ResidentialApartmentsList, ResidentialFloorDetail, CustomSelect, Legend, CustomDropdown, PageTitle, Filters, ProjectLoadRevit },
  data() {
    return {
      selectedData: null,
      isFetching: false,
      legendFetching: false,
      legendInfoClicked: false,
      dropdownLoading: false,
      floorsPlan: [],
      legendData: [],
      floorParamData: [],
      smetaLoading: {},
      showReasonDialog: false,
      selectedReason: null,
      reasonOptions: [],
      pendingFloors: [],
      versionComments: '',
      avrLockedWorks: [],
      recalculableWorks: [],
      avrLoading: false,
      avrExpanded: {},
      filters: {
        resident_id: '',
        project_type_id: '',
        project_entrance_id: '',
      },
      floorsMismatch: null,
      floorsMismatchLoading: false,
      loadRevitDialogOpen: false,
    };
  },
  mounted() {
    // Прокручиваем страницу наверх при загрузке
    window.scrollTo(0, 0)
    
    // Загружаем список причин изменения сметы
    this.getReasons()
    
    // Восстанавливаем фильтры из URL при загрузке страницы
    const query = this.$route.query
    if (query.resident_id) {
      this.filters.resident_id = parseInt(query.resident_id) || ''
    }
    if (query.project_type_id) {
      this.filters.project_type_id = parseInt(query.project_type_id) || ''
    }
    if (query.project_entrance_id) {
      this.filters.project_entrance_id = parseInt(query.project_entrance_id) || ''
      // Если есть все фильтры, загружаем данные
      if (this.filters.resident_id && this.filters.project_type_id) {
        this.getFloorsPlan()
      }
    }
    
    // Восстанавливаем выбранную квартиру из URL
    if (query.flat_id && query.floor_map_id) {
      this.selectedData = {
        ...this.filters,
        flat_id: parseInt(query.flat_id),
        floor_map_id: parseInt(query.floor_map_id),
        remont_id: query.remont_id ? parseInt(query.remont_id) : null
      }
    }
  },
  methods: {
    handleAction(action) {
      if(!action?.onClick) return
      action.onClick()
    },
    getFloorData(floor = '') {
      if(!this.floorParamData?.length) return ''
      const data = this.floorParamData?.find(item => item.floor === floor)
      if(!data) return ''
      return data?.param_value || ''
    },
    onFLoorsPlanUpdated(res) {
      this.floorsPlan = res
    },
    async handleRefresh() {
      await this.getFloorsPlan();
    },
    async getFloorsPlan() {
      if(!this.filters.project_entrance_id || !this.filters.project_type_id) {
        this.floorsPlan = null
        this.legendData = null
        this.floorParamData = null
        return
      }
      this.isFetching = true
      const res = await getEntranceApartments(this.filters)
      this.isFetching = false
      this.floorsPlan = res?.data || []
      this.loadFloorsMismatch()
    },
    async loadFloorsMismatch() {
      if (!this.filters.project_entrance_id || !this.filters.resident_id || !this.filters.project_type_id) {
        this.floorsMismatch = null
        return
      }
      this.floorsMismatchLoading = true
      const data = await getEntranceFloorsMismatch({
        resident_id: this.filters.resident_id,
        project_type_id: this.filters.project_type_id,
        project_entrance_id: this.filters.project_entrance_id,
      })
      this.floorsMismatchLoading = false
      this.floorsMismatch = data || null
    },
    async getLegend() {
      if(!this.filters.project_entrance_id || !this.filters.project_type_id) {
        this.legendData = null
        this.floorParamData = null
        return
      }
      this.legendFetching = true
      const res = await getEntranceLegendInfo(this.filters)
      this.legendFetching = false
      this.legendInfoClicked = true
      if(!res) return
      this.legendData = res?.data
      this.floorParamData = res?.floor_param_value
    },
    async getReasons() {
      const res = await getSmetaChangeReasons()
      if (res) {
        this.reasonOptions = res
      }
    },
    async recalculateFloorsSmeta() {
      const floors = this.floorsPlan?.filter(item => item.is_selected)
      if(floors?.length) {
        this.pendingFloors = floors
        this.showReasonDialog = true
        this.loadAvrLockedWorks()
      }
    },
    async loadAvrLockedWorks() {
      if (!this.filters.project_entrance_id) return
      
      this.avrLoading = true
      const floor_map_ids = this.pendingFloors.map(f => f.floor_map_id)
      
      const [avrWorks, recalcWorks] = await Promise.all([
        getAvrLockedWorks(this.filters.project_entrance_id, floor_map_ids),
        getRecalculableWorks(this.filters.project_entrance_id, floor_map_ids)
      ])
      
      this.avrLockedWorks = avrWorks
      this.recalculableWorks = recalcWorks
      this.avrLoading = false
    },
    async recalcProjectMaterialPrices() {
      if(!this.selectedData?.project_id) return showErrorToast('Не найден ID проекта')
      this.showModal(MODAL_NAMES.RecalcProjectMaterialPrices, {
        title: 'Пересчет цен материалов',
        project_id: this.selectedData?.project_id,
        floor_map_ids: this.floorsPlan?.filter(item => item.is_selected)?.map(item => item.floor_map_id) || null,
        onSubmit: () => {
          this.floorsPlan = this.floorsPlan.map(item => ({...item, is_selected: false}))
          this.showModal(MODAL_NAMES.SmetaRecalcSuccess, {
            title: '',
            message: 'Цены пересчитаны успешно',
            description: 'Необходимо создать доп. соглашение',
            btnText: 'Создать доп. соглашение',
            onSubmit: () => this.handleCreateDS(),
          })
        },
      })
    },
    cancelRecalc() {
      this.showReasonDialog = false
      this.selectedReason = null
      this.versionComments = ''
      this.pendingFloors = []
      this.avrLockedWorks = []
      this.recalculableWorks = []
    },
    formatNumber(num) {
      if (!num) return '0'
      return Number(num).toLocaleString('ru-RU', { maximumFractionDigits: 2 })
    },
    formatDate(dateStr) {
      if (!dateStr) return 'дата не указана'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    async confirmRecalc() {
      if (!this.selectedReason) return
      
      this.showReasonDialog = false
      const reasonId = this.selectedReason
      
      if(this.pendingFloors?.length) {
        // Используем Promise.all для ожидания завершения всех пересчетов
        const recalcPromises = this.pendingFloors.map(({floor_map_id}) => {
          return this.recalculate(floor_map_id, reasonId)
        })
        let notif = null
        try {
          notif = this.$q.notify({
            message: 'Пересчет сметы...',
            color: 'deep-orange',
            spinner: true,
            position: 'top',
            timeout: 100000
          })
          await Promise.all(recalcPromises)
          
          // После завершения всех пересчетов вызываем создание версии сметы
          const selectedReasonObj = this.reasonOptions.find(r => r.smeta_change_reason_id === reasonId)
          if (selectedReasonObj && this.pendingFloors[0]?.floor_map_id) {
            await createSmetaVersion({
              floor_map_id: this.pendingFloors[0].floor_map_id,
              reason_code: selectedReasonObj.reason_code,
              comments: this.versionComments || null
            })
            notif && notif()
            this.showModal(MODAL_NAMES.SmetaRecalcSuccess, {
              title: '',
              message: 'Сметы пересчитаны успешно',
              description: 'Необходимо создать доп. соглашение',
              btnText: 'Создать доп. соглашение',
              onSubmit: () => this.handleCreateDS(),
            })
          }
        } catch (error) {
          console.error('Ошибка при пересчете смет:', error)
          notif && notif()
        }
      }
      
      this.selectedReason = null
      this.versionComments = ''
      this.pendingFloors = []
      this.avrLockedWorks = []
    },
    handleCreateDS() {
      const project_id = this.effectiveProjectId
      const finish_date = this.selectedData?.finish_date

      this.showSecondaryModal(MODAL_NAMES.CONFIRM, {
        title: 'Сменить дату завершения проекта',
        cancelBtnText: 'Оставить и создать ДС',
        btnText: 'Сменить',
        onSubmit: async () => {
          this.showSecondaryModal(MODAL_NAMES.ChangeProjectDate, {
            title: 'Сменить дату завершения проекта',
            body: {
              project_id,
              finish_date,
            },
            onSubmit: () => {
              this.onFLoorSchemaUpdated()
            },
          })
        },
        onReject: async () => {
          const res = await createDS({ project_finish_date: null, project_id })
          if (res) {
            this.showSucceedSnackbar()
            this.closeSecondaryModal()
            this.onFLoorSchemaUpdated()
            this.closeModal()
          }
        },
      })
    },
    async recalculate(floor_map_id, smeta_change_reason_id = null) {
      if(this.smetaLoading[floor_map_id]) return
      this.smetaLoading[floor_map_id] = true
      const res = await recalculateSmeta({floor_map_id, smeta_change_reason_id})
      this.smetaLoading[floor_map_id] = false
      if(!res) return
      this.floorsPlan = this.floorsPlan.map(item => {
        if(item.floor_map_id === floor_map_id)
          return {...item, ...res}
        return item
      })
    },
    async createObject(type) {
      if(this.dropdownLoading) return
      this.dropdownLoading = true
      const res = await createFloorObject(type, this.filters.project_entrance_id)
      this.dropdownLoading = false
      if(!res) return
      this.floorsPlan = res
      this.showSucceedSnackbar()
    },
    async handleDeleteFloor(floor) {
      if (!floor?.floor_map_id) return
      this.$q.dialog({
        title: 'Удаление этажа',
        message: `Удалить этаж ${floor.floor}? Это действие нельзя отменить.`,
        cancel: { label: 'Отмена', flat: true },
        ok: { label: 'Удалить', color: 'negative' },
        persistent: true
      }).onOk(async () => {
        const res = await deleteProjectFloor(floor.floor_map_id, {})
        if (!res) {
          this.showErrorSnackbar('Ошибка при удалении этажа')
          return
        }
        this.showSucceedSnackbar('Этаж удалён')
        await this.getFloorsPlan()
        await this.loadFloorsMismatch()
      })
    },
    handleGrouping(wall) {
      this.showModal(MODAL_NAMES.RESIDENTIAL_GROUPING, {
        title: 'Смена группировки этажей',
        data: wall,
        mode: 'grouping',
        params: {...this.filters},
        onSubmit: (res) => {
          this.floorsPlan = res?.data
          this.legendData = res?.floor_param_types || []
        },
      })
    },
    onFiltersChange(key, value, row) {
      this.filters[key] = value
      if(!!row)
        this.selectedData = this.selectedData ? {...this.selectedData, ...row} : {...row}

      if(key === 'resident_id') {
        this.filters = {resident_id: value}
        this.floorsPlan = null
        this.legendData = null
        this.floorParamData = null
        if(!value)
          this.selectedData = null
      }
      if(key === 'project_type_id') {
        this.filters.project_entrance_id = ''
        this.legendData = null
        this.floorParamData = null
      }
      if(key === 'project_entrance_id') {
        this.legendData = null
        this.floorParamData = null
        this.getFloorsPlan()
      }
      
      // Обновляем URL с текущими фильтрами
      this.updateURL()
    },
    updateURL() {
      // Формируем query параметры из текущих фильтров
      const query = {}
      if (this.filters.resident_id) query.resident_id = this.filters.resident_id
      if (this.filters.project_type_id) query.project_type_id = this.filters.project_type_id
      if (this.filters.project_entrance_id) query.project_entrance_id = this.filters.project_entrance_id
      
      // Добавляем параметры выбранной квартиры если она открыта
      if (this.selectedData?.flat_id) {
        query.flat_id = this.selectedData.flat_id
        query.floor_map_id = this.selectedData.floor_map_id
        if (this.selectedData.remont_id) {
          query.remont_id = this.selectedData.remont_id
        }
      }
      
      // Обновляем URL без перезагрузки страницы
      this.$router.replace({ query }).catch(() => {})
    },
    onApartmentClick(apartment) {
      this.selectedData = {...this.selectedData, ...apartment}
      // Обновляем URL с параметрами квартиры
      this.updateURL()
    },
    onFLoorSchemaUpdated() {
      this.getFloorsPlan()
      if(this.legendInfoClicked)
        this.getLegend()
    },
    resetSelected() {
      this.selectedData = {...this.selectedData, flat_id: null, floor_map_id: null}
      // Убираем параметры квартиры из URL
      this.updateURL()
    },
    onResidentsLoaded(residents) {
      // Обогащаем selectedData названием ЖК
      if (this.selectedData && this.selectedData.resident_id && !this.selectedData.resident_name) {
        const resident = residents.find(r => r.resident_id === this.selectedData.resident_id)
        if (resident) {
          this.selectedData = {
            ...this.selectedData,
            resident_name: resident.resident_name
          }
        }
      }
    },
    onProjectTypesLoaded(projectTypes) {
      // Обогащаем selectedData названием типа проекта
      if (this.selectedData && this.selectedData.project_type_id && !this.selectedData.project_type_name) {
        const projectType = projectTypes.find(pt => pt.project_type_id === this.selectedData.project_type_id)
        if (projectType) {
          this.selectedData = {
            ...this.selectedData,
            project_type_name: projectType.project_type_name
          }
        }
      }
    },
    onEntrancesLoaded(entrances) {
      // Обогащаем selectedData названием подъезда
      if (this.filters?.project_entrance_id) {
        const entrance = entrances?.find(e => e.project_entrance_id === this.filters.project_entrance_id)
        if (entrance) {
          this.selectedData = {
            ...this.selectedData,
            entrance_name: entrance.entrance_name,
            entrance: entrance.entrance,
            floor: entrance.floor,
            project_id: entrance.project_id,
          }
        }
      }
    }
  },
  computed: {
    avrSummary() {
      if (!this.avrLockedWorks?.length) return { worksCount: 0, totalSum: 0 }
      // Используем первую строку для получения агрегатов
      const first = this.avrLockedWorks[0]
      return {
        worksCount: first?.grand_works_count || this.avrLockedWorks.length,
        totalSum: first?.grand_total_sum || 0
      }
    },
    avrGroupedByDocument() {
      if (!this.avrLockedWorks?.length) return []
      
      const grouped = {}
      this.avrLockedWorks.forEach(work => {
        const key = work.floor_map_document_id
        if (!grouped[key]) {
          grouped[key] = {
            floor_map_document_id: work.floor_map_document_id,
            avr_code: work.avr_code,
            avr_date: work.avr_date,
            total_sum: work.avr_total_sum,
            works_count: work.avr_works_count,
            works: []
          }
        }
        grouped[key].works.push(work)
      })
      
      // Сортируем по дате (новые сверху)
      return Object.values(grouped).sort((a, b) => {
        if (!a.avr_date) return 1
        if (!b.avr_date) return -1
        return new Date(b.avr_date) - new Date(a.avr_date)
      })
    },
    recalculableSummary() {
      if (!this.recalculableWorks?.length) return { worksCount: 0, totalSum: 0 }
      const first = this.recalculableWorks[0]
      return {
        worksCount: first?.grand_works_count || this.recalculableWorks.length,
        totalSum: first?.grand_total_sum || 0
      }
    },
    recalculableGroupedByFloor() {
      if (!this.recalculableWorks?.length) return []
      
      const grouped = {}
      this.recalculableWorks.forEach(work => {
        const key = work.floor_map_id
        if (!grouped[key]) {
          grouped[key] = {
            floor_map_id: work.floor_map_id,
            floor: work.floor,
            total_sum: work.floor_total_sum,
            works_count: work.floor_works_count,
            works: []
          }
        }
        grouped[key].works.push(work)
      })
      
      // Сортируем по этажу (сверху вниз)
      return Object.values(grouped).sort((a, b) => b.floor - a.floor)
    },
    smetaNeverCalculated() {
      return !this.avrLoading && 
             this.avrLockedWorks?.length === 0 && 
             this.recalculableWorks?.length === 0
    },
    isFloorSelected() {
      return this.floorsPlan?.some(item => item.is_selected)
    },
    effectiveProjectId() {
      return this.selectedData?.project_id ?? null
    },
    actions() {
      return [
         { label: 'Подгрузить Revit проект', value: 'loadRevit', disabled: !this.effectiveProjectId || !this.filters.project_entrance_id, onClick: () => { this.loadRevitDialogOpen = true }, hideWhenClick: true },
         {label: 'Изменить группировку', value: 'fill-params', disabled: !this.filters.project_entrance_id || !this.floorsPlan, onClick: this.handleGrouping, hideWhenClick: true},
         {label: 'Пересчитать смету', value: 'recalculate', disabled: !this.isFloorSelected, onClick: this.recalculateFloorsSmeta, hideWhenClick: true},
         {label: 'Создать подвал', value: 'createBasement', onClick: () => this.createObject('BASEMENT')},
         {label: 'Создать кровлю', value: 'createRoof', onClick: () => this.createObject('ROOF')},
         {label: 'Пересчитать цены материалов и работ', value: 'recalcProjectMaterialPrices', onClick: () => this.recalcProjectMaterialPrices(), hideWhenClick: true},
      ]
    },
  },
  setup() {
    const {showModal, closeModal, showSecondaryModal, closeSecondaryModal} = useAppStore()
    const {showSucceedSnackbar, showErrorSnackbar} = useCustomSnackbar();
    return {showSucceedSnackbar, showErrorSnackbar, showModal, closeModal, showSecondaryModal, closeSecondaryModal};
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  padding-top: 20px;
  position: relative;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 15px;
  padding: 0 20px;
}
.filters {
  display: flex;
  align-items: flex-end;
  gap: 15px;
}
.filters__item {
  width: 220px;
}

.floors-mismatch-placeholder {
  padding: 8px 0;
  min-height: 36px;
}

.floors-mismatch-enter-active {
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
}
.floors-mismatch-leave-active {
  transition: opacity 0.2s ease-in, transform 0.2s ease-in;
}
.floors-mismatch-enter-from,
.floors-mismatch-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.floors-mismatch-enter-to,
.floors-mismatch-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.project-id-badge {
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--q-negative);
  padding: 4px 12px;
  border-radius: 6px;
  background: rgba(198, 40, 40, 0.08);
  border: 1px solid rgba(198, 40, 40, 0.25);
}

.load-revit-dialog-card {
  width: 50vw;
  max-width: 50vw;
  height: 50vh;
  max-height: 50vh;
  display: flex;
  flex-direction: column;
}

.load-revit-dialog-body {
  flex: 1;
  overflow: auto;
  min-height: 0;
}
</style>
